import { env_backend_url } from 'env_Variables';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

export default function useData<T>(endPoint: string): {
  data: T | null;
  loading: boolean;
  error: any;
  refetch: () => void;
} {
  const { token } = useSelector((state: RootState) => state);
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(false);

  const request = useCallback(
    (signal: AbortSignal | undefined, apply: (json: T | null, err: unknown) => void) => {
      if (!endPoint) return;
      setLoading(true);
      setError(false);
      fetch(`${env_backend_url}${endPoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
          return response.json();
        })
        .then((json: T) => apply(json, null))
        .catch((err) => {
          // Aborts are expected on unmount / re-fetch, not real errors.
          if (err?.name === 'AbortError') return;
          apply(null, err);
        });
    },
    [endPoint, token]
  );

  const settle = useCallback((json: T | null, err: unknown) => {
    if (err) {
      setError(err);
      setData(null);
    } else {
      setData(json);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let ignore = false;
    request(controller.signal, (json, err) => {
      if (!ignore) settle(json, err);
    });
    return () => {
      ignore = true;
      controller.abort();
    };
  }, [request, settle]);

  const refetch = useCallback(() => {
    request(undefined, settle);
  }, [request, settle]);

  return { data, loading, error, refetch };
}
